import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useReactToPrint } from 'react-to-print';
import { IoPrintOutline } from 'react-icons/io5';
import { MdOutlinePerson, MdOutlinePersonOff } from 'react-icons/md';
import GaugeComponent from 'react-gauge-component';
import moment from 'moment';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Spinner from 'assets/gif/loading.gif';
import axios from 'config/axios';
import Header from 'components/Header';
import Button from 'components/Button';
import ButtonLink from 'components/ButtonLink';
import LevelBar from 'components/LevelBar';
import Table from 'components/Table';
import Dropdown from 'components/Dropdown2';
import Dropdown4 from 'components/Dropdown4';
import Loader from 'components/Loader';
import ScoreWithScale from 'components/ScoreWithScale2';
import useWindowSize from 'hooks/useWindowSize';
import QuestionPopup from 'components/QuestionPopup';

import './index.css';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const timePerQuestionEnglish = 35 * 60 / 50;
const timePerQuestionMath = 70 * 60 / 44;    
const timePerQuestionReading = 40 * 60 / 36;
const timePerQuestionScience = 40 * 60 / 36;
const CONFIGURATION= {
    'ACT English': 'E',
    'ACT Mathematics': 'M',
    'ACT Reading': 'R',
    'ACT Science': 'S'
}

function ClassACTReport () {
    const [isQuestionPopupOpen, setIsQuestionPopupOpen] = useState(false);
    const [questionId, setQuestionId] = useState(null);
    const [correctStudent, setCorrectStudent] = useState(0);
    const [currentQuestionData, setCurrentQuestionData] = useState(null);
    const [testType, setTestType] = useState('');
    const [barWidth, setBarWidth] = useState(0);
    const [fontSize, setFontSize] = useState(80);
    const [scoreChartData, setScoreChartData] = useState<{ range: string; students: number }[]>([]);
    const [testData, setTestData] = useState(null);
    const [modules, setModules] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [subjectIndex, setSubjectIndex] = useState(0);
    const [downloadInProgress, setDownloadInProgress] = useState(false);
    const [printInProgress, setPrintInProgress] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [hideStudentNames, setHideStudentNames] = useState(false);

    const windowSize = useWindowSize();
    const { classId, testId } = useParams();
    const printRef = useRef(null);
    const questionPopupRef = useOutsideClickHandler(() => handleExit(false));
    const barRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
      const pathParts = location.pathname.split('/');
      const testType = pathParts[2]; // ex: 'digital-act' or 'digital-sat'
      setTestType(testType);
    }, [location]);

    const exportStudentTestData = () => {
        setDownloadInProgress(true);
        axios({
            url: `tutors/report/download/${classId}/${testId}/`,
            method: 'GET',
            responseType: 'blob',
        })
        .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${testData?.className}-${testData?.testName}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setDownloadInProgress(false);
        })
        .catch(error => {
            console.error('Error downloading CSV:', error);
            setDownloadInProgress(false);
        });
    }

    const printBatchReport = () => {
        setPrintInProgress(true);
        axios.get(`tutors/report/print/${classId}/${testId}`)
            .then(res => {
                setReportData(res.data.studentTests);
                setPrintInProgress(false);
            })
            .catch(error => {
                console.log(error);
                setPrintInProgress(false);
            });

    }

    const handleQuestionPopup = (questionId: string) => {
        setQuestionId(questionId);
        axios.get(`tutors/classes/report/question/${questionId}/${testId}/`)
            .then(res => {
                setCurrentQuestionData(res.data)
            })
            .catch(error => {
                console.log(error);
            });

        setIsQuestionPopupOpen(true);
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const formatTime2 = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        return hours 
            ? `${hours} hr ${minutes} min ${seconds} sec`
            : minutes
            ? `${minutes} min ${seconds} sec`
            : `${seconds} sec`;
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current
    });

    const getScoreDistribution = (studentScores) => {
        const scoreBins = [
          { label: "< 18", min: 1, max: 17 },
          { label: "18 - 19", min: 18, max: 19 },
          { label: "20 - 21", min: 20, max: 21 },
          { label: "22 - 23", min: 22, max: 23 },
          { label: "24 - 25", min: 24, max: 25 },
          { label: "26 - 27", min: 26, max: 27 },
          { label: "28 - 29", min: 28, max: 29 },
          { label: "30 - 31", min: 30, max: 31 },
          { label: "32 - 33", min: 32, max: 33 },
          { label: "34 - 35", min: 34, max: 35 },
          { label: "36", min: 36, max: 36 }
        ];
      
        const validScores = studentScores.filter(score => score !== null && score !== undefined);
      
        const bins = scoreBins.map(bin => ({
          range: bin.label,
          students: 0,
        }));
      
        validScores.forEach(score => {
          for (let i = 0; i < scoreBins.length; i++) {
            if (score >= scoreBins[i].min && score <= scoreBins[i].max) {
              bins[i].students += 1;
              break;
            }
          }
        });
      
        return bins;
    };

    const handleExit = (isExit: boolean) => {
        setIsQuestionPopupOpen(isExit);
        setCurrentQuestionData(null);
    }

    useEffect(()=>{
        if (testData && questionId) {
            let correctCount = 0;
            let taken = 0;

            testData.students.forEach(student => {
                student.moduleAnswers.forEach(moduleAnswer => {
                    const answer = moduleAnswer.answers.find(a => a.questionId === questionId);
                    if (answer) {
                        taken +=1;
                        if (answer.isCorrect) {
                            correctCount += 1;
                        }
                    }
                });
            });

            setCorrectStudent(taken>0? (correctCount/taken)*100: null);
        }
    }, [testData, questionId]);

    useEffect(() => {
        if (!testType) return;

        axios.get(`tutors/classes/${classId}/${testType}/report/${testId}/`)
            .then(res => {
                setTestData(res.data);

                let modulesArray = [];
                let subjectsArray = [];
                for (let module of res.data.modules) {
                    modulesArray.push(module.subject)
                }
                setModules(modulesArray);
                if (res.data.hasCompositeScore) {
                    subjectsArray.push('Composite')
                }
                if (res.data.hasStemScore) {
                    subjectsArray.push('STEM')
                }
                for (let subject of res.data.configTestSections) {
                    subjectsArray.push(subject)
                }
                setSubjects(subjectsArray);
                const firstSubject = subjectsArray[0];
                let data;
                if (firstSubject==='Composite') {
                    data = getScoreDistribution(
                        res.data.studentCompositeScores
                    );
                } else if (firstSubject==='STEM') {
                    data = getScoreDistribution(
                        res.data.studentStemScores
                    );
                } else if (firstSubject==='ACT English') {
                    data = getScoreDistribution(
                        res.data.studentEnglishScores
                    );
                } else if (firstSubject==='ACT Mathematics') {
                    data = getScoreDistribution(
                        res.data.studentMathScores
                    );
                } else if (firstSubject==='ACT Reading') {
                    data = getScoreDistribution(
                        res.data.studentReadingScores
                    );
                } else if (firstSubject==='ACT Science') {
                    data = getScoreDistribution(
                        res.data.studentScienceScores
                    );
                }
                setScoreChartData(data)
            })
            .catch(error => {
                console.log(error);
            });
    }, [testType]);

    useEffect(()=>{
        const selected = subjects[subjectIndex]
        let data;
        if (selected==='Composite') {
            data = getScoreDistribution(
                testData.studentCompositeScores
            );
        } else if (selected==='STEM') {
            data = getScoreDistribution(
                testData.studentStemScores
            );
        } else if (selected==='ACT English') {
            data = getScoreDistribution(
                testData.studentEnglishScores
            );
        } else if (selected==='ACT Mathematics') {
            data = getScoreDistribution(
                testData.studentMathScores
            );
        } else if (selected==='ACT Reading') {
            data = getScoreDistribution(
                testData.studentReadingScores
            );
        } else if (selected==='ACT Science') {
            data = getScoreDistribution(
                testData.studentScienceScores
            );
        }
        setScoreChartData(data)
    }, [subjectIndex]);

    useEffect(() => {
        if (reportData) {
            handlePrint();
        }
    }, [reportData]);

    if (testData === null) return <Loader />;

    return (
        <div className='classACTReport'>
            <Helmet>
                <title>
                    Class Report - Test Center Admin
                </title>
            </Helmet>
            {(isQuestionPopupOpen && testData) &&
                <QuestionPopup
                    info={currentQuestionData?.info}
                    correctStudent={correctStudent!==null? `${correctStudent.toFixed(1)}%`: '-'}
                    testName={testData?.testName}
                    question={currentQuestionData?.question}
                    passages={currentQuestionData?.passages}
                    figures={currentQuestionData?.figures}
                    choices={currentQuestionData?.choices}
                    correctChoice={currentQuestionData?.correctChoice}
                    correctAnswers={currentQuestionData?.correctAnswers}
                    manageExit={() => handleExit(false)}
                    innerRef={questionPopupRef}
                />
            }
            <Header
                title={(testData?.testName && testData?.configTestSections?.length>0)? (testData?.configTestSections?.length<4?`${testData.testName} (${testData.configTestSections.map(section=> CONFIGURATION[section]).join(', ')})`:`${testData.testName}`) : null}
                prevRoutes={testData && [{ name: testData.className, path: `/classes/${classId}` }]}
            />
            <div className='classACTReport__subHeaderRow'>
                <h2 className='classACTReport__subHeader'>
                    Scores by students
                </h2>
                <div className='classReport__buttonGroup'>
                    <Button className='classReport__button' secondary onClick={() => setHideStudentNames(!hideStudentNames)}>
                        {hideStudentNames
                        ?   <>
                                <MdOutlinePerson className='classReport__buttonIcon' />
                                <p>Show</p>
                            </>
                        :   <>
                                <MdOutlinePersonOff className='classReport__buttonIcon' />
                                <p>Hide</p>
                            </>
                        }
                    </Button>
                    <Button className='classReport__button' onClick={printBatchReport}>
                        {printInProgress
                        ?   <img src={Spinner} />
                        :   <>
                                <IoPrintOutline className='classReport__buttonIcon' />
                                <p>Report</p>                            
                            </>
                        }
                    </Button>
                </div>
            </div>
            <div className='classACTReport__scoresContainer'>
                <Table
                    className='classACTReport__table'
                    header={[
                        'Students',
                        ...testData.classTestSections.map((classTestSection, p) => (
                            <p className='classACTReport__cellCenter'>
                                {classTestSection.replace(
                                    'ACT ', ''
                                )}
                            </p>
                        )),
                        <p className='classACTReport__cellCenter'>Composite Score</p>,
                        <p className='classACTReport__cellCenter'>STEM Score</p>,
                        <p className='classACTReport__cellCenter'>Progress</p>
                    ]}
                    columnTypes={[
                        'string',
                        ...Array(testData.classTestSections.length).fill('number'),
                        'number',
                        'number',
                        null
                      ]}
                    rows={testData.students.map(student => {
                        // const firstScoreIndex = student.testScores.findIndex(score => score !== null);
                        // const lastScoreIndex = student.testScores.findLastIndex(score => score !== null);
                        // const progress = 100*(student.testScores[lastScoreIndex]-student.testScores[firstScoreIndex])/student.testScores[firstScoreIndex]

                        return [
                            hideStudentNames ? null : `${student.firstName} ${student.lastName}`,
                            ...testData.classTestSections.map((subjectName) => (
                                (() => {
                                    const testSection = student.test?.testSections.find(
                                    (section) => section.subject === subjectName
                                    );
                                    return testSection?.score ? Math.floor(testSection.score) : null;
                                })()
                            )),
                            student.test.compositeScore ? Math.floor(student.test.compositeScore) : null,
                            student.test.stemScore ? Math.floor(student.test.stemScore) : null,
                            <div className='classACTReport__progress classACTReport__cellCenter'>
                                {new Array(student.test.testModuleCount).fill(null).map((_count, c) => (
                                    <div className={student.test.completedModuleCount > c
                                        ? 'classACTReport__progressBar--filled'
                                        : (student.test.inProgress && c === 0)
                                        ? 'classACTReport__progressBar--started'
                                        : 'classACTReport__progressBar'
                                    }></div>
                                ))}
                            </div>
                        ];
                    })}
                    onSelectCell={testData.students.map(student => ([
                        hideStudentNames ? null : () => navigate(`/students/${student.studentId}`),
                        ...testData.classTestSections.map(() => null),
                        student.test.compositeScore ? () => navigate(`/report/digital-act/${student.test.testId}`): null,
                        null,
                        null,
                    ]))}
                    navigator={false}
                />
                <div className='classACTReport__scoreDistribution'>
                    <div className='classACTReport__scoreDistribution__header'>
                        <div className='classACTReport__scoreDistribution__title'>Score distribution</div>
                        <Dropdown4
                            width={110}
                            className='classACTReport__dropdown'
                            data={subjects}
                            onSelect={value => setSubjectIndex(subjects.findIndex(module => module === value))}
                            value={subjects[subjectIndex]}
                        />
                    </div>
                    <div className='classACTReport__scoreDistribution__content'>
                        <div className='classACTReport__scoreDistribution__graph'>
                            <ResponsiveContainer>
                                <BarChart data={scoreChartData} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                                    <XAxis
                                    dataKey="range"
                                    tick={{ fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                    label={{
                                        value: 'Score',
                                        position: 'insideBottom',
                                        offset: -10,
                                        style: {
                                        fontSize: 12,
                                        fill: '#6B7280',
                                        fontWeight: 500,
                                        },
                                    }}
                                    />
                                    <YAxis
                                    allowDecimals={false}
                                    domain={[0, 'dataMax + 1']}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 12 }}
                                    />
                                    <Tooltip />
                                    <Bar dataKey="students" fill="#4F46E5" radius={[4, 4, 0, 0]} minPointSize={2}/>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className='classACTReport__scoreDistribution__scores'>
                            <div className='classACTReport__scoreDistribution__avgScore'>
                                <div className='classACTReport__scoreDistribution__avg'>Avg Composite</div>
                                <div className='classACTReport__scoreDistribution__avgScoreNum'>{testData?.avgComScore}</div>
                            </div>
                            <div className='classACTReport__scoreDistribution__avgScore'>
                                <div className='classACTReport__scoreDistribution__avg'>Avg STEM</div>
                                <div className='classACTReport__scoreDistribution__avgScoreNum'>{testData?.avgStemScore}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='classACTReport__category'>
                    <div className='classACTReport__scoreDistribution__title'>Category breakdown (%correct)</div>
                    <div className='classACTReport__scoreDistribution__analysisWrap'>
                        {Object.keys(testData.avgCorrectPercent).length > 0 ? (
                            Object.entries(testData.avgCorrectPercent).map(([subject, categories], index) => (
                                <div key={index} className='classACTReport__analysisBox'>
                                    <div className='classACTReport__analysisBoxTitle'>{subject.replace('ACT ', '')}</div>
                                    <div className='classACTReport__analysisContentWrap'>
                                        {Object.entries(categories)?.map(([categoryName, answerNumInfo], i) => {
                                            const { numCorrect, numQuestions } = answerNumInfo;
                                            const percent = numQuestions > 0 ? (numCorrect / numQuestions) * 100 : 0;
                                            return (
                                                <div key={i} className='classACTReport__categoryWrap'>
                                                    <div className='classACTReport__categoryTitle'>{categoryName}</div>
                                                    <LevelBar className={subject==='ACT English'
                                                                        ? 'LevelBar__greenBox'
                                                                        : subject==='ACT Mathematics'
                                                                        ? 'LevelBar__orangeBox'
                                                                        : subject==='ACT Reading'
                                                                        ? 'LevelBar__redBox'
                                                                        : 'LevelBar__blueBox'
                                                                        } 
                                                                percent = {percent}/>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))
                        ): (
                            <div className='classACTReport__analysisBox'>
                                <div className='classACTReport__analysisBoxTitle'>-</div>
                                <div className='classACTReport__analysisContentWrap'>
                                <div className='classACTReport__categoryWrap'>
                                    <div className='classACTReport__categoryTitle'>-</div>
                                    <LevelBar className='LevelBar__defaultBox' percent={0} />
                                </div>
                                </div>
                            </div>
                            )
                        }
                    </div>
                </div>
            </div>
            <div className='classACTReport__subHeaderRow'>
                <h2 className='classACTReport__subHeader'>
                    Student responses
                </h2>
                <Dropdown
                    className='classACTReport__dropdown'
                    data={modules}
                    onSelect={value => setModuleIndex(modules.findIndex(module => module === value))}
                    value={modules[moduleIndex]}
                />
                <div className='classACTReport__buttonGroup'>
                    {/* <Button className='classACTReport__button' onClick={printBatchReport}>
                        {printInProgress ? <img src={Spinner} /> : 'Print'}
                    </Button> */}
                    <Button className='classACTReport__button' onClick={exportStudentTestData}>
                        {downloadInProgress ? <img src={Spinner} /> : 'Download'}
                    </Button>
                </div>
            </div>
            <Table
                fixHeader
                header={['Students', ...testData.modules[moduleIndex].questions.map((question, q) => (
                    <p className={`
                        classACTReport__cellCenter
                        classACTReport__response 
                        classACTReport__cursor
                        classACTReport__tableHeader
                    `}
                    onClick={() => handleQuestionPopup(question[1])}>
                        Q{question[0]}
                    </p>
                ))]}
                rows={[
                    [
                        <p className='classACTReport__header'># Incorrect</p>,
                        ...testData.modules[moduleIndex].questions.map((_question, q) => (
                            <p className='classACTReport__header classACTReport__cellCenter'>
                                {testData.students.filter(student => (
                                    student.moduleAnswers.find(moduleAnswer => (
                                        moduleAnswer.subject === testData.modules[moduleIndex].subject
                                        && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                                        && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                                    ))
                                )).filter(student => (
                                    !student.moduleAnswers.find(moduleAnswer => (
                                        moduleAnswer.subject === testData.modules[moduleIndex].subject
                                        && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                                        && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                                    )).answers[q].isCorrect
                                )).length
                            }</p>
                        ))
                    ],
                    ...testData.students.filter(student => (
                        student.moduleAnswers.find(moduleAnswer => (
                            moduleAnswer.subject === testData.modules[moduleIndex].subject
                            && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                            && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                        ))
                    )).map(student => ([
                        hideStudentNames ? null : `${student.firstName} ${student.lastName}`,
                        ...student.moduleAnswers.find(moduleAnswer => (
                            moduleAnswer.subject === testData.modules[moduleIndex].subject
                            && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                            && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                        )).answers.map(answer => (
                            <p className={`classACTReport__cellCenter classACTReport__response ${!answer.isCorrect && (answer.answer
                                ? 'classACTReport__cellWrong'
                                : 'classACTReport__cellSkipped'
                            )}`}>
                                {answer.answer ? answer.answer : '-'}
                            </p>
                        ))
                    ])),
                ]}
                navigator={false}
            />
            {reportData &&
                <div className='classACTReport__report' ref={printRef}>
                    {reportData.map(testData => (<>
                        <div className='classACTReport__headerGroup'>
                            <div className='classACTReport__headerSection'>
                                {testData.logo && 
                                    <img
                                        className='classACTReport__headerLogo'
                                        alt='logo'
                                        src={testData.logo}
                                    />
                                }
                            </div>
                            <div className='classACTReport__headerSection'>
                                <div className='classACTReport__headerInfo'>
                                    <p>
                                        Name: {testData.studentName}
                                    </p>
                                    <p>
                                        Email: {testData.email}
                                    </p>
                                    <p>
                                        Test name: {testData.name}
                                    </p>
                                    <p>
                                        Tested on: {moment(testData.testDate).format('MMMM D YYYY')}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className='classACTReport__headerGroup'>
                            <h1 className='classACTReport__headerTitle'>
                                Test Result Overview
                            </h1>
                            {/* <div className='classACTReport__buttonGroup'>
                                <Button className='classACTReport__button' secondary onClick={handlePrint}>
                                    PRINT REPORT
                                </Button>
                                <ButtonLink className='classACTReport__button' to={`/review/digital-act/${testId}`}>
                                    REVIEW QUESTIONS
                                </ButtonLink>
                            </div> */}
                        </div>
                        <div className='classACTReport__card'>
                            {/* test scores */}
                            <div className='classACTReport__scoresGroup'>
                                {testData.compositeScore && <>
                                    <div className='classACTReport__scoresGroupBox'>
                                        <div className='classACTReport__scoreTitleBoxCom'>
                                            <h3 className='classACTReport__scoreTitle'>COMPOSITE SCORE</h3>
                                        </div>
                                        <div className='classACTReport__scoreContent'>
                                            <ScoreWithScale
                                                className='classACTReport__scoreWithScale'
                                                score={Math.round(testData.compositeScore)}
                                                scoreMin={Math.round(testData.minScore)}
                                                scoreMax={Math.round(testData.maxScore)}
                                                fontSize={70}
                                                // fontSize={windowSize.width > 576
                                                //     ? 100
                                                //     : 70
                                                // }
                                            />
                                            <div className="classACTReport__graphWrap">
                                                <div className="classACTReport__graphLocation" style={{ left: `${((Math.round(testData.compositeScore) - 1) / 35) * barWidth}px` }}>
                                                    <div className="classACTReport__graph">you</div>
                                                    <div className="triangle"></div>
                                                </div>
                                                <div className="classACTReport__graphBar" ref={barRef}/>
                                                <div className="classACTReport__maxScore">
                                                    {Math.round(testData.maxScore)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>}
                                {testData.stemScore && <>
                                    <div className='classACTReport__scoresGroupBoxStem'>
                                        <div className='classACTReport__scoreTitleBoxStem'>
                                            <h3 className='classACTReport__scoreTitle'>STEM SCORE</h3>
                                        </div>
                                        <div className='classACTReport__scoreContent'>
                                            <ScoreWithScale
                                                className='classACTReport__scoreWithScale'
                                                score={Math.round(testData.stemScore)}
                                                scoreMin={Math.round(testData.minScore)}
                                                scoreMax={Math.round(testData.maxScore)}
                                                fontSize={70}
                                                // fontSize={windowSize.width > 576
                                                //     ? 100
                                                //     : 70
                                                // }
                                            />
                                            <div className="classACTReport__graphWrap">
                                                <div className="classACTReport__graphLocation" style={{ left: `${((Math.round(testData.stemScore) - 1) / 35) * barWidth}px` }}>
                                                    <div className="classACTReport__graph">you</div>
                                                    <div className="triangle"></div>
                                                </div>
                                                <div className="classACTReport__graphBarStem" />
                                                <div className="classACTReport__maxScore">
                                                    {Math.round(testData.maxScore)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>}
                            </div>

                            {/* subject info */}
                            {testData.testSections.map((testSection, s) => {
                                const testModule = testData.testModules.find(module => module.subject === testSection.subject);
                                const correctCount = testModule ? testModule.answers.filter(answer => answer.isCorrect).length : '-';
                                const totalCount = testModule ? testModule.answers.length : '-';
                                const timeSpent = testModule ? testModule.answers.reduce((total, answer) => total + answer.timeSpent, 0) : null;

                                return (
                                    <div className='classACTReport__insideCard'>
                                        <div className='classACTReport__ScoreBox'>
                                            <div className='classACTReport__sectionScore'>
                                                <h3 className='classACTReport__sectionScoreTitle'>
                                                    {testSection.subject.replace('ACT ', '').replace('Mathematics', 'Math')}
                                                </h3>
                                                <ScoreWithScale
                                                    className='classACTReport__scoreWithScale'
                                                    score={Math.round(testSection.score)}
                                                    scoreMin={Math.round(testSection.minScore)}
                                                    scoreMax={Math.round(testSection.maxScore)}
                                                    fontSize={fontSize}
                                                />
                                            </div>
                                            <div className='classACTReport__rowScore'>
                                                <div className='classACTReport__infoTitle'>Raw score</div>
                                                <table className='classACTReport__overviewTable'>
                                                    <tbody>
                                                        <tr>
                                                            <th>Correct</th>
                                                            <td>{correctCount}/{totalCount}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Time</th>
                                                            <td>{timeSpent !== null ? formatTime(timeSpent) : '-'}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <div className='classACTReport__sectionInfo'>
                                            <table className='classACTReport__categoryInfo'>
                                                <tr>
                                                    <th>By Category</th>
                                                    <th>Section Time</th>
                                                    <th>Time Per Question</th>
                                                </tr>
                                                {testSection.rootCategories.map(rootCategory => (<>
                                                    <tr>
                                                        <td>
                                                            <h5 className='classACTReport__levelLabel'>
                                                                {rootCategory.name}
                                                            </h5>
                                                            <LevelBar
                                                                className={testSection.subject==='ACT English'
                                                                    ? 'LevelBar__greenBox'
                                                                    : testSection.subject==='ACT Mathematics'
                                                                    ? 'LevelBar__orangeBox'
                                                                    : testSection.subject==='ACT Reading'
                                                                    ? 'LevelBar__redBox'
                                                                    : 'LevelBar__blueBox'
                                                                    } 
                                                                percent={100*rootCategory.numCorrect/rootCategory.numQuestions}
                                                                label={`${rootCategory.numCorrect}/${rootCategory.numQuestions}`}
                                                            />
                                                        </td>
                                                        <td>{formatTime2(rootCategory.timeSpent)}</td>
                                                        <td>
                                                            <GaugeComponent
                                                                className='classACTReport__timeGauge'
                                                                type='semicircle'
                                                                value={rootCategory.timeSpent/rootCategory.numQuestions}
                                                                minValue={0}
                                                                maxValue={testSection.subject === 'ACT English'
                                                                    ?   timePerQuestionEnglish * 3
                                                                    :   testSection.subject === 'ACT Mathematics'
                                                                    ?   timePerQuestionMath * 3
                                                                    :   testSection.subject === 'ACT Reading'
                                                                    ?   timePerQuestionReading * 3
                                                                    :   testSection.subject === 'ACT Science'
                                                                    ?   timePerQuestionScience * 3
                                                                    :   180
                                                                }
                                                                arc={{
                                                                    'subArcs': [
                                                                        { length: 0.33, color: "#5BE12C"},
                                                                        { length: 0.33, color: "#F5CD19"},
                                                                        { length: 0.33, color: "#EA4228"}
                                                                    ]
                                                                }}
                                                                pointer={{
                                                                    'width': 10,
                                                                }}
                                                                labels={{
                                                                    'valueLabel': {
                                                                        'hide': true
                                                                    },
                                                                    'tickLabels': {
                                                                        'hideMinMax': true
                                                                    }
                                                                }}
                                                            />
                                                            <p>{formatTime2(rootCategory.timeSpent/rootCategory.numQuestions)}</p>
                                                        </td>
                                                    </tr>
                                                </>))}
                                            </table>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        <div className='classACTReport__printBreak'></div>
                    </>))}
                </div>
            }
        </div>
    );
}

export default ClassACTReport;